/**
 * "10:24 PM local time · Same time zone as you".
 *
 * Everything here goes through `Intl`, never through a hand-rolled offset
 * table: an offset is a property of an INSTANT in a zone, not of the zone, so
 * anything cached across a DST boundary is wrong twice a year. `Intl` also
 * rejects a zone the runtime does not know, which is the only honest way to
 * validate the free-text `users.timezone` column.
 */

/** What the API falls back to when a user has no zone of their own. */
export const DEFAULT_TIME_ZONE = 'Asia/Kolkata';

export interface LocalTimeInfo {
  /** e.g. "10:24 PM". */
  readonly clock: string;
  /** The zone actually used, after validation. */
  readonly timeZone: string;
  /** True when it matches the viewer's own zone — Teams says so explicitly. */
  readonly sameAsViewer: boolean;
}

/** The viewer's own zone, as the browser reports it. */
export function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIME_ZONE;
}

/** `true` only when this runtime can actually format in `timeZone`. */
function isUsableZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

/**
 * Two zones can carry different names and still be the same wall clock
 * (Asia/Kolkata vs Asia/Calcutta). Comparing the FORMATTED time rather than the
 * identifier is what makes "Same time zone as you" true in that case.
 */
function sameWallClock(a: string, b: string, at: Date): boolean {
  if (a === b) return true;
  const format = (zone: string): string =>
    new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
    }).format(at);
  return format(a) === format(b);
}

/**
 * Resolve someone's local clock. `timeZone` is whatever the API gave us, which
 * may be empty or nonsense; an unusable value degrades to the tenant default
 * rather than throwing inside a render.
 */
export function localTimeFor(timeZone: string | null | undefined, now: Date = new Date()): LocalTimeInfo {
  const candidate = timeZone !== null && timeZone !== undefined && timeZone !== '' ? timeZone : DEFAULT_TIME_ZONE;
  const zone = isUsableZone(candidate) ? candidate : DEFAULT_TIME_ZONE;
  const clock = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(now);
  return { clock, timeZone: zone, sameAsViewer: sameWallClock(zone, viewerTimeZone(), now) };
}

/** "08:00" -> "8:00 AM". Formats a bare wall-clock time with no date attached. */
export function formatTimeOfDay(value: string): string | null {
  const match = /^(\d{1,2}):(\d{2})/.exec(value);
  if (match === null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || hours > 23 || !Number.isInteger(minutes) || minutes > 59) return null;
  const suffix = hours < 12 ? 'AM' : 'PM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${suffix}`;
}

/** Clock time of an instant, rendered in a specific zone. */
export function clockAt(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso));
}
